import { useMemo, useState, useEffect, useRef, useCallback, Component } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import DOMPurify from "dompurify";

const BASE_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ script: "sub" }, { script: "super" }],
  [{ color: [] }, { background: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ indent: "-1" }, { indent: "+1" }],
  [{ align: [] }],
  ["blockquote", "link", "image"],
  ["clean"],
];

const FORMATS = ["header", "bold", "italic", "underline", "strike", "script", "color", "background", "list", "indent", "align", "blockquote", "link", "image"];
const FORMATS_CODE = [...FORMATS, "code-block", "code"];

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

export const isEmptyHtml = (html) => {
  if (!html) return true;
  if (/<img\s/i.test(html)) return false;
  const t = html.replace(/<(.|\n)*?>/g, "").replace(/&nbsp;/g, " ").trim();
  return t.length === 0;
};

const htmlToPlain = (html) => {
  if (!html) return "";
  return String(html)
    .replace(/<\/(p|div|br|li|h[1-6])>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<(.|\n)*?>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};
const plainToHtml = (txt) => {
  if (!txt) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return txt.split(/\n{2,}/).map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("");
};

// Rete di sicurezza: se Quill non riesce a inizializzarsi, mostra una textarea funzionante.
class EditorErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[RichEditor] Editor avanzato non disponibile, uso fallback textarea:", error, info?.componentStack);
  }
  render() {
    if (this.state.failed) {
      const { value, onChange, placeholder, testid, readOnly } = this.props;
      if (readOnly) {
        return <div className="rich-editor-readonly rounded-sm border border-border bg-secondary/20 p-3 text-sm min-h-[100px] whitespace-pre-wrap">{htmlToPlain(value)}</div>;
      }
      return (
        <div className="rich-editor" data-testid={testid}>
          <textarea
            data-testid={testid ? `${testid}-fallback` : "rich-editor-fallback"}
            className="w-full min-h-[150px] rounded-sm border border-border p-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder={placeholder}
            defaultValue={htmlToPlain(value)}
            onChange={(e) => onChange && onChange(plainToHtml(e.target.value))}
          />
          <p className="text-[11px] text-muted-foreground mt-1">Editor semplificato attivo su questo browser.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function QuillEditor({ value, onChange, placeholder, testid, onImageUpload, mentionUsers, onMentionUser, codeBlock }) {
  const wrapRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange); onChangeRef.current = onChange;
  const imgUpRef = useRef(onImageUpload); imgUpRef.current = onImageUpload;
  const onMentionRef = useRef(onMentionUser); onMentionRef.current = onMentionUser;
  const lastHtmlRef = useRef(value || "");
  const [mentionMenu, setMentionMenu] = useState(null);
  const enableMentions = Array.isArray(mentionUsers);

  const insertImage = useCallback(async (file) => {
    const upload = imgUpRef.current; const q = quillRef.current;
    if (!file || !upload || !q) return;
    const range = q.getSelection(true);
    const idx = range ? range.index : q.getLength();
    const ph = "  [caricamento immagine…]  ";
    try {
      q.insertText(idx, ph);
      const url = await upload(file);
      q.deleteText(idx, ph.length);
      if (url) { q.insertEmbed(idx, "image", url); q.setSelection(idx + 1); }
    } catch { q.deleteText(idx, ph.length); }
  }, []);

  // Inizializzazione una sola volta
  useEffect(() => {
    if (!wrapRef.current || quillRef.current) return;
    const wrap = wrapRef.current;
    const container = codeBlock ? [...BASE_TOOLBAR.slice(0, -1), ["code-block"], ["clean"]] : BASE_TOOLBAR;
    const inner = document.createElement("div");
    wrap.appendChild(inner);
    const q = new Quill(inner, {
      theme: "snow", placeholder,
      modules: { toolbar: { container } },
      formats: codeBlock ? FORMATS_CODE : FORMATS,
    });
    quillRef.current = q;
    if (value) { try { q.clipboard.dangerouslyPasteHTML(value); } catch { /* noop */ } lastHtmlRef.current = q.root.innerHTML; }

    if (imgUpRef.current) {
      const tb = q.getModule("toolbar");
      tb.addHandler("image", () => {
        const input = document.createElement("input");
        input.type = "file"; input.accept = "image/*";
        input.onchange = () => { if (input.files?.[0]) insertImage(input.files[0]); };
        input.click();
      });
    }

    const emit = () => {
      const html = q.root.innerHTML;
      lastHtmlRef.current = html;
      onChangeRef.current && onChangeRef.current(isEmptyHtml(html) ? "" : html);
    };
    q.on("text-change", emit);

    const onPaste = (e) => {
      if (!imgUpRef.current) return;
      const items = e.clipboardData?.items || [];
      for (const it of items) if (it.type?.startsWith("image/")) { const f = it.getAsFile(); if (f) { e.preventDefault(); insertImage(f); return; } }
    };
    const onDrop = (e) => {
      if (!imgUpRef.current) return;
      const files = e.dataTransfer?.files || [];
      for (const f of files) if (f.type?.startsWith("image/")) { e.preventDefault(); insertImage(f); return; }
    };
    q.root.addEventListener("paste", onPaste);
    q.root.addEventListener("drop", onDrop);

    let mentionCleanup = null;

    return () => {
      try {
        q.off("text-change", emit);
        q.root.removeEventListener("paste", onPaste);
        q.root.removeEventListener("drop", onDrop);
        if (mentionCleanup) mentionCleanup();
      } catch { /* noop */ }
      quillRef.current = null;
      if (wrap) wrap.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Menzioni: (ri)collega i listener quando enableMentions diventa true (il composer parte in 'rispondi')
  useEffect(() => {
    const q = quillRef.current;
    if (!q || !enableMentions) return;
    const check = () => {
      const domSel = window.getSelection();
      if (!domSel || domSel.rangeCount === 0) { setMentionMenu(null); return; }
      const range = domSel.getRangeAt(0);
      if (!q.root.contains(range.startContainer)) { setMentionMenu(null); return; }
      const pre = range.cloneRange();
      pre.selectNodeContents(q.root);
      pre.setEnd(range.startContainer, range.startOffset);
      const before = pre.toString();
      const m = before.match(/(?:^|\s)@([\p{L}0-9._'-]*)$/u);
      setMentionMenu(m ? { query: (m[1] || "").toLowerCase() } : null);
    };
    const deferred = () => requestAnimationFrame(check);
    q.on("text-change", deferred);
    q.on("selection-change", deferred);
    q.root.addEventListener("keyup", deferred);
    return () => {
      try { q.off("text-change", deferred); q.off("selection-change", deferred); q.root.removeEventListener("keyup", deferred); } catch { /* noop */ }
      setMentionMenu(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableMentions]);

  // Placeholder reattivo (cambia tra 'rispondi' e 'nota')
  useEffect(() => {
    const q = quillRef.current;
    if (q && q.root) q.root.dataset.placeholder = placeholder || "";
  }, [placeholder]);

  // Sincronizza modifiche esterne del valore (senza sovrascrivere la digitazione)
  useEffect(() => {
    const q = quillRef.current; if (!q) return;
    if ((value || "") !== (lastHtmlRef.current || "")) {
      try { q.clipboard.dangerouslyPasteHTML(value || ""); } catch { /* noop */ }
      lastHtmlRef.current = q.root.innerHTML;
    }
  }, [value]);

  const mentionCandidates = mentionMenu
    ? (mentionUsers || []).filter((u) => {
        const query = mentionMenu.query;
        if (!query) return true;
        return (u.name || "").toLowerCase().includes(query) || (u.email || "").toLowerCase().includes(query);
      }).slice(0, 8)
    : [];

  const pickMention = (u) => {
    const q = quillRef.current; if (!q) return;
    const sel = q.getSelection();
    const caret = sel ? sel.index : q.getLength() - 1;
    const text = q.getText(0, caret);
    const at = text.lastIndexOf("@");
    if (at < 0) { setMentionMenu(null); return; }
    q.deleteText(at, caret - at);
    const token = `@${u.name}`;
    q.insertText(at, token, { color: "#2563eb", bold: true });
    q.insertText(at + token.length, " ", { color: false, bold: false });
    q.setSelection(at + token.length + 1, 0);
    setMentionMenu(null);
    onMentionRef.current && onMentionRef.current(u);
  };

  return (
    <div className="rich-editor relative" data-testid={testid}>
      <div ref={wrapRef} />
      {enableMentions && mentionMenu && mentionCandidates.length > 0 && (
        <div className="absolute z-30 left-2 top-full mt-1 w-72 max-h-56 overflow-y-auto rounded-sm border border-border bg-white shadow-lg" data-testid="mention-menu">
          {mentionCandidates.map((u) => (
            <button key={u.id} type="button" onMouseDown={(e) => { e.preventDefault(); pickMention(u); }} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-secondary text-sm" data-testid={`mention-option-${u.id}`}>
              <span className="flex flex-col leading-tight"><span className="font-medium text-slate-700">{u.name}</span>{u.email && <span className="text-[11px] text-muted-foreground">{u.email}</span>}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function RichEditor({ value, onChange, placeholder, testid, readOnly = false, onImageUpload, mentionUsers, onMentionUser, codeBlock = false }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  if (readOnly) {
    return (
      <EditorErrorBoundary value={value} onChange={onChange} placeholder={placeholder} testid={testid} readOnly>
        <div data-testid={testid}><SafeHtml html={value} className="rich-editor-readonly rounded-sm border border-border bg-secondary/20 p-3 text-sm min-h-[100px]" /></div>
      </EditorErrorBoundary>
    );
  }
  if (!mounted) return <div className="rich-editor" data-testid={testid} style={{ minHeight: 172 }} />;
  return (
    <EditorErrorBoundary value={value} onChange={onChange} placeholder={placeholder} testid={testid}>
      <QuillEditor value={value} onChange={onChange} placeholder={placeholder} testid={testid}
        onImageUpload={onImageUpload} mentionUsers={mentionUsers} onMentionUser={onMentionUser} codeBlock={codeBlock} />
    </EditorErrorBoundary>
  );
}

export function SafeHtml({ html, className }) {
  const clean = useMemo(() => DOMPurify.sanitize(html || "", { USE_PROFILES: { html: true }, ADD_ATTR: ["target"] }), [html]);
  return <div className={className} dangerouslySetInnerHTML={{ __html: clean }} />;
}
